<template>
  <base-tile
    :title="$t('entitlements.usage.title')"
    :editMode="props.editMode"
    i18nPrefix="entitlements"
    :rows="state.rows"
  >
    <!-- Seats -->
    <template #[state.readSeatsSlot]>
      <td class="p-2">{{ state.seats }}</td>
    </template>
    <template #[state.editSeatsSlot]>
      <td class="edit-seats">
        <va-input
          v-model="state.seats"
          @change="updateEntilementSeatsField('seats', $event.target.value)"
          :rules="[v => validateSeats(v)]"
          :error-messages="$t('entitlements.seatsError')"
        />
      </td>
    </template>

    <!-- allowance -->
    <template #[state.readAllowanceSlot]>
      <td class="p-2">
        <span>{{ state.allowance }}</span>
        <span v-if="state.usageUnit" class="ml-1">{{ state.usageUnit }}</span>
      </td>
    </template>
    <template #[state.editAllowanceSlot]>
      <td class="p-2">
        <va-input
          v-model="state.allowance"
          @update:model-value="
            updateEntitlementEditField(
              'usage.allowance',
              Number(state.allowance)
            )
          "
          :rules="[v => validateAllowance(Number(v))]"
          :error-messages="$t('entitlements.allowanceError')"
          type="number"
        />
        <span v-if="state.usageUnit" class="ml-1">{{ state.usageUnit }}</span>
      </td>
    </template>

    <!-- remaining allowance -->
    <template #[state.readRemainingAllowanceSlot]>
      <td class="p-2">
        <span>{{ state.remainingAllowance }}</span>
        <span v-if="state.usageUnit" class="ml-1">{{ state.usageUnit }}</span>
      </td>
    </template>
    <template #[state.editRemainingAllowanceSlot]>
      <td class="p-2">
        <va-input
          v-model="state.remainingAllowance"
          @update:model-value="
            updateEntitlementEditField(
              'usage.remainingAllowance',
              Number(state.remainingAllowance)
            )
          "
          :rules="[v => validateAllowance(Number(v))]"
          :error-messages="$t('entitlements.allowanceError')"
          type="number"
        />
        <span v-if="state.usageUnit" class="ml-1">{{ state.usageUnit }}</span>
      </td>
    </template>

    <!-- assigned allowance -->
    <template #[state.readAssignedAllowanceSlot]>
      <td class="p-2">
        <span>{{ state.assignedAllowance }}</span>
        <span v-if="state.usageUnit" class="ml-1">{{ state.usageUnit }}</span>
      </td>
    </template>

    <!-- last credited at -->
    <template #[state.readLastCreditedAtSlot]>
      <td class="p-2">
        {{ new Date(state.lastCreditedAt).toLocaleString() }}
      </td>
    </template>

    <!-- usage unit -->
    <template #[state.readUsageUnitSlot]>
      <td class="p-2">{{ state.usageUnit }}</td>
    </template>

    <!-- usage description -->
    <template #[state.readUsageDescriptionSlot]>
      <td class="p-2">{{ state.usageDescription }}</td>
    </template>

    <!-- License Type -->
    <template #[state.editLicenseTypeSlot]>
      <td class="p-2">
        <va-select
          v-model="state.licenseType"
          :options="LICENSE_TYPES"
          @update:model-value="
            updateEntitlementEditField('licenseType', state.licenseType)
          "
        />
      </td>
    </template>

    <!-- LICENSE ID -->
    <template #[state.readLicenseIdSlot]>
      <td v-if="state.licenseId" class="p-2">
        <span class="identifier">{{ state.licenseId }} </span>
        <button-clipboard-copy
          :field="$t('entitlements.license.id')"
          :value="state.licenseId"
        />
      </td>
    </template>

    <!-- LICENSE KEY -->
    <template #[state.readLicenseKeySlot]>
      <td v-if="state.licenseKey" class="p-2">
        <span class="identifier">{{ state.licenseKey }} </span>
        <button-clipboard-copy
          :field="$t('entitlements.license.key')"
          :value="state.licenseKey"
        />
      </td>
    </template>

    <!-- LICENSE KEY MASKED -->
    <template #[state.editIsKeyMaskedSlot]>
      <td class="p-2">
        <va-select
          v-model="state.isKeyMaskedDisplay"
          :options="[$t('general.yes'), $t('general.no')]"
          @update:model-value="
            (state.isKeyMasked =
              state.isKeyMaskedDisplay === $t('general.yes'));
            updateEntitlementEditField('license', {
              ...store.state.licensing.entitlementEdit.license,
              isKeyMasked: state.isKeyMasked
            })
          "
        />
      </td>
    </template>

    <!-- Owner Type -->
    <template #[state.editOwnerTypeSlot]>
      <td class="p-2">
        <span>{{ state.ownerType }} </span>
        <va-button
          class="ml-2"
          flat
          size="small"
          icon="edit"
          @click="openEditOwnerModal"
        />
        <va-button
          v-if="state.ownerId"
          class="ml-2"
          flat
          size="small"
          icon="delete"
          @click="openConfirmOwnerDeletion"
        />
      </td>
    </template>

    <!-- Owner ID -->
    <template #[state.readOwnerIdSlot]>
      <td class="p-2">
        <span v-if="state.isOwnerInvalid" class="invalid-owner">
          <va-icon name="warning" color="danger" />
          <span class="identifier invalid-text">{{ state.ownerId }}</span>
          <span class="ml-2 invalid-text">{{
            $t("entitlements.invalidOwner")
          }}</span>
        </span>
        <span v-else-if="state.ownerId" class="identifier">{{
          state.ownerId
        }}</span>
        <span v-else>-</span>
        <button-clipboard-copy
          v-if="
            state.ownerId &&
              state.ownerId !== 'unknown' &&
              !state.isOwnerInvalid
          "
          :field="$t('entitlements.ownerId')"
          :value="state.ownerId"
        />
      </td>
    </template>
    <template #[state.editOwnerIdSlot]>
      <td class="p-2">
        <span v-if="state.isOwnerInvalid" class="invalid-owner">
          <va-icon name="warning" color="danger" />
          <span class="invalid-text">{{ state.ownerId }}</span>
          <span class="ml-2 invalid-text">{{
            $t("entitlements.invalidOwner")
          }}</span>
        </span>
        <span v-else-if="state.ownerId">{{ state.ownerId }}</span>
        <span v-else>-</span>
        <va-button
          class="ml-2"
          flat
          size="small"
          icon="edit"
          @click="openEditOwnerModal"
        />
        <va-button
          v-if="state.ownerId"
          class="ml-2"
          flat
          size="small"
          icon="delete"
          @click="openConfirmOwnerDeletion"
        />
      </td>
    </template>

    <!-- Organization name -->
    <template #[state.orgNameSlot]>
      <td
        v-if="
          state.ownerType === 'Organization' &&
            state.ownerId &&
            state.ownerId !== 'unknown' &&
            state.organization &&
            state.organization.id &&
            !state.isOwnerInvalid
        "
        class="pl-2"
      >
        <span>{{ state.organization.name }} </span>
        <router-link
          v-if="state.organization.id"
          :to="{ path: organizationLink(state.organization.id) }"
        >
          <va-button class="ml-2" flat icon="arrow_forward" />
        </router-link>
      </td>
    </template>

    <!-- User name -->
    <template #[state.userNameSlot]>
      <td
        v-if="
          state.ownerType === 'User' &&
            state.ownerId &&
            state.ownerId !== 'unknown' &&
            state.user &&
            state.user.id &&
            !state.isOwnerInvalid
        "
        class="pl-2"
      >
        <span>
          {{ state.user.displayName }}
        </span>
        <router-link
          v-if="state.user.id"
          :to="{ path: userLink(state.user.id) }"
        >
          <va-button class="ml-2" flat icon="arrow_forward" />
        </router-link>
      </td>
    </template>
  </base-tile>

  <!-- Change owner modal -->
  <change-owner-modal
    licensingObject="entitlement"
    :isOpen="state.editOwnerModalIsOpen"
    :entityType="state.ownerType"
    @cancel="handleModalCancel"
    @validate="handleModalValidate"
  />

  <!-- Change owner modal -->
  <delete-entitlement-owner-modal
    :isOpen="state.confirmOwnerDeletion"
    @cancel="state.confirmOwnerDeletion = false"
    @validate="confirmOwnerDeletion"
  />

  <!-- Owner validation error message -->
  <va-alert
    v-if="state.isOwnerInvalid && props.editMode"
    color="danger"
    class="mt-3"
    border="left"
    icon="warning"
  >
    {{ $t("entitlements.invalidOwnerEditModeError") }}
  </va-alert>
</template>

<style scoped>
.identifier {
  font-family: monospace;
  font-size: 0.9rem;
}

.invalid-owner {
  display: flex;
  align-items: center;
}

.invalid-text {
  color: var(--va-danger);
  font-weight: 500;
}
</style>

<script setup>
import { computed, reactive, watch } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import BaseTile from "@/components/BaseTile.vue";
import ButtonClipboardCopy from "@/components/ButtonClipboardCopy.vue";
import ChangeOwnerModal from "@/components/modal/ChangeOwnerModal.vue";
import DeleteEntitlementOwnerModal from "@/components/modal/DeleteEntitlementOwnerModal.vue";
import { useI18nValue } from "@/composable";
import { LICENSE_TYPES } from "@/constants";
import { organizationLink, userLink } from "@/utils/identity";
import { callFetchUser, callFetchOrganization } from "@/api/identity";

const { t } = useI18n();
const store = useStore();

const emit = defineEmits(["endEditMode", "updateEntitlement"]);

const props = defineProps({
  editMode: {
    type: Boolean,
    required: true
  },
  seatPolicy: {
    type: String,
    required: true
  }
});

const state = reactive({
  editOwnerModalIsOpen: false,
  confirmOwnerDeletion: false,
  // Multi-counter entitlements show their usage in EntitlementCountersTile instead
  hasCounters:
    (store.state.licensing.entitlementEdit.usages?.length ?? 0) > 0,
  seats: store.state.licensing.entitlementEdit.seats,
  allowance: store.state.licensing.entitlementEdit.usage?.allowance,
  remainingAllowance:
    store.state.licensing.entitlementEdit.usage?.remainingAllowance,
  assignedAllowance:
    store.state.licensing.entitlementEdit.usage?.assignedAllowance,
  lastCreditedAt: store.state.licensing.entitlementEdit.usage?.lastCreditedAt,
  usageType: store.state.licensing.entitlementEdit.usage?.type,
  usageUnit: store.state.licensing.entitlementEdit.usage?.unit,
  usageDescription: store.state.licensing.entitlementEdit.usage?.description,
  deactivationCount:
    store.state.licensing.entitlementEdit.license?.deactivationCount ??
    undefined,
  licenseId: store.state.licensing.entitlementEdit.license?.id,
  licenseKey: store.state.licensing.entitlementEdit.license?.key,
  licenseType: store.state.licensing.entitlementEdit.license?.type,
  isKeyMasked:
    store.state.licensing.entitlementEdit.license?.isKeyMasked ?? null,
  isKeyMaskedDisplay:
    store.state.licensing.entitlementEdit.license?.isKeyMasked === true
      ? t("general.yes")
      : t("general.no"),
  activationCount:
    store.state.licensing.entitlementEdit.license?.activationCount ?? 0,
  ownerType: store.state.licensing.entitlementEdit.ownerType,
  ownerId: store.state.licensing.entitlementEdit.ownerId,
  organization: store.state.identity.organization,
  user: store.state.identity.user,
  isOwnerInvalid: computed(() => {
    // Owner is invalid if we have an ownerId and ownerType but no corresponding user/organization data
    if (!state.ownerId || state.ownerId === "unknown" || !state.ownerType) {
      return false;
    }

    if (state.ownerType === "User") {
      return !state.user || !state.user.id;
    }

    if (state.ownerType === "Organization") {
      return !state.organization || !state.organization.id;
    }

    return false;
  }),
  rows: computed(() => {
    return [
      {
        label: t("entitlements.seats"),
        value: state.seats,
        editable: true
      },
      ...(state.deactivationCount !== undefined
        ? [
            {
              label: t("entitlements.deactivationCount"),
              value: state.deactivationCount
            }
          ]
        : []),
      ...(state.activationCount !== undefined
        ? [
            {
              label: t("entitlements.activationCount"),
              value: state.activationCount
            }
          ]
        : []),
      ...(!state.hasCounters && state.usageType === "Credits"
        ? [
            {
              label: t("entitlements.usageType"),
              value: state.usageType
            },
            {
              label: t("entitlements.allowance"),
              value: state.allowance,
              editable: true
            },
            {
              label: t("entitlements.remainingAllowance"),
              value: state.remainingAllowance,
              editable: true
            },
            {
              label: t("entitlements.assignedAllowance"),
              value: state.assignedAllowance
            },
            ...(state.usageUnit
              ? [
                  {
                    label: t("entitlements.usageUnit"),
                    value: state.usageUnit
                  }
                ]
              : []),
            ...(state.lastCreditedAt
              ? [
                  {
                    label: t("entitlements.lastCreditedAt"),
                    value: new Date(state.lastCreditedAt).toLocaleString()
                  }
                ]
              : []),
            ...(state.usageDescription
              ? [
                  {
                    label: t("entitlements.usageDescription"),
                    value: state.usageDescription
                  }
                ]
              : [])
          ]
        : []),
      ...(!state.hasCounters &&
      state.allowance !== undefined &&
      state.usageType !== "Credits"
        ? [
            {
              label: t("entitlements.allowance"),
              value: state.allowance,
              editable: true
            }
          ]
        : []),
      {
        label: t("entitlements.license.id"),
        value: state.licenseId
      },
      {
        label: t("entitlements.license.type"),
        value: useI18nValue(state.licenseType, "licenseType")
      },
      {
        label: t("entitlements.license.key"),
        value: state.licenseKey
      },
      ...(state.licenseKey
        ? [
            {
              label: t("entitlements.isKeyMasked"),
              value: state.isKeyMasked ? t("general.yes") : t("general.no"),
              editable: true
            }
          ]
        : []),
      {
        label: t("entitlements.ownerType"),
        value: useI18nValue(state.ownerType, "entityType"),
        editable: true
      },
      {
        label: t("entitlements.ownerId"),
        value: state.ownerId,
        editable: true
      },
      ...(state.ownerType === "Organization" && !state.isOwnerInvalid
        ? [
            {
              label: t("users.organizationName"),
              value: state.organization.name,
              editable: false
            }
          ]
        : []),
      ...(state.ownerType === "User" && !state.isOwnerInvalid
        ? [
            {
              label: t("users.email"),
              value: state.user.email,
              editable: false
            }
          ]
        : []),
      ...(state.ownerType === "User" && !state.isOwnerInvalid
        ? [
            {
              label: t("users.name"),
              value: state.user.displayName,
              editable: false
            }
          ]
        : [])
    ];
  }),
  readLicenseIdSlot: `${t("entitlements.license.id")}-value`,
  readLicenseTypeSlot: `${t("entitlements.license.type")}-value`,
  readLicenseKeySlot: `${t("entitlements.license.key")}-value`,
  editIsKeyMaskedSlot: `${t("entitlements.isKeyMasked")}-edit-value`,
  readSeatsSlot: `${t("entitlements.seats")}-value`,
  editSeatsSlot: `${t("entitlements.seats")}-edit-value`,
  editAllowanceSlot: `${t("entitlements.allowance")}-edit-value`,
  readAllowanceSlot: `${t("entitlements.allowance")}-value`,
  editOwnerTypeSlot: `${t("entitlements.ownerType")}-edit-value`,
  editLicenseTypeSlot: `${t("entitlements.license.type")}-edit-value`,
  readOwnerIdSlot: `${t("entitlements.ownerId")}-value`,
  editOwnerIdSlot: `${t("entitlements.ownerId")}-edit-value`,
  orgNameSlot: `${t("users.organizationName")}-value`,
  userNameSlot: `${t("users.name")}-value`,
  readRemainingAllowanceSlot: `${t("entitlements.remainingAllowance")}-value`,
  editRemainingAllowanceSlot: `${t(
    "entitlements.remainingAllowance"
  )}-edit-value`,
  readAssignedAllowanceSlot: `${t("entitlements.assignedAllowance")}-value`,
  readLastCreditedAtSlot: `${t("entitlements.lastCreditedAt")}-value`,
  readUsageUnitSlot: `${t("entitlements.usageUnit")}-value`,
  readUsageDescriptionSlot: `${t("entitlements.usageDescription")}-value`
});

watch(
  () => props.editMode,
  (editMode, prevEditMode) => {
    // Reset state based on the store value when we cancel or confirm the edition
    if (prevEditMode === true && editMode === false) {
      state.user = store.state.identity.user;
      state.organization = store.state.identity.organization;
      state.seats = store.state.licensing.entitlementEdit.seats;
      state.allowance = store.state.licensing.entitlementEdit.usage?.allowance;
      state.remainingAllowance =
        store.state.licensing.entitlementEdit.usage?.remainingAllowance;
      state.assignedAllowance =
        store.state.licensing.entitlementEdit.usage?.assignedAllowance;
      state.lastCreditedAt =
        store.state.licensing.entitlementEdit.usage?.lastCreditedAt;
      state.usageType = store.state.licensing.entitlementEdit.usage?.type;
      state.usageUnit = store.state.licensing.entitlementEdit.usage?.unit;
      state.usageDescription =
        store.state.licensing.entitlementEdit.usage?.description;
      state.seatsAssigned = store.state.licensing.entitlementEdit.seatsAssigned;
      state.allowedLicenseTypes =
        store.state.licensing.entitlementEdit.allowedLicenseTypes;
      state.isKeyMasked =
        store.state.licensing.entitlementEdit.license?.isKeyMasked ?? null;
      state.isKeyMaskedDisplay =
        store.state.licensing.entitlementEdit.license?.isKeyMasked === true
          ? t("general.yes")
          : t("general.no");
      state.offlineActivationMode =
        store.state.licensing.entitlementEdit.offlineActivationMode;
      state.allowVirtualMachines = store.state.licensing.entitlementEdit
        .allowVirtualMachines
        ? t("general.yes")
        : t("general.no");
      state.manageable = store.state.licensing.entitlementEdit.manageable
        ? t("general.yes")
        : t("general.no");
      state.ownerType = store.state.licensing.entitlementEdit.ownerType;
      state.ownerId = store.state.licensing.entitlementEdit.ownerId;
    }
  }
);

watch(
  [
    () => store.state.licensing.entitlementEdit.ownerId,
    () => store.state.licensing.entitlementEdit.ownerType
  ],
  async ([newOwnerId, newOwnerType]) => {
    state.ownerId = newOwnerId;
    state.ownerType = newOwnerType;
    if (!newOwnerId || newOwnerId === "unknown") {
      state.user = undefined;
      state.organization = undefined;
      return;
    }
    if (newOwnerType === "User") {
      try {
        const resp = await callFetchUser(newOwnerId);
        state.user = resp?.data || undefined;
      } catch {
        state.user = undefined;
      }
      state.organization = undefined;
    } else if (newOwnerType === "Organization") {
      try {
        const resp = await callFetchOrganization(newOwnerId);
        state.organization = resp?.data || undefined;
      } catch {
        state.organization = undefined;
      }
      state.user = undefined;
    } else {
      state.user = undefined;
      state.organization = undefined;
    }
  }
);

const handleModalCancel = () => {
  state.editOwnerModalIsOpen = false;
};

const handleModalValidate = () => {
  state.editOwnerModalIsOpen = false;
};

const openEditOwnerModal = () => {
  state.editOwnerModalIsOpen = true;
};

const openConfirmOwnerDeletion = () => {
  state.confirmOwnerDeletion = true;
};

// Update the seats field
const updateEntilementSeatsField = (key, value) => {
  if (validateSeats(value)) {
    if (
      store.state.licensing.entitlement.manageable &&
      state.seats !== store.state.licensing.entitlementEdit.seats
    ) {
      store.dispatch("licensing/updateEntitlementEditField", {
        key: "manageable",
        value: true
      });
    }
    updateEntitlementEditField(key, parseSeats(value));
  }
};

// Validate the seats field
const validateSeats = value => {
  if (props.seatPolicy === "None") {
    return true;
  }
  const seats = Number.parseInt(value, 10);

  if (props.seatPolicy === "Optional") {
    if (value === null || value === undefined || value === "") {
      return true;
    }

    return !Number.isNaN(seats) && seats >= 0;
  }
  if (props.seatPolicy === "Always") {
    return !Number.isNaN(seats) && seats >= 0;
  }
};

const parseSeats = value => {
  if (props.seatPolicy === "None") {
    return null;
  }
  if (props.seatPolicy === "Optional") {
    if (value === null || value === undefined || value === "") {
      return null;
    }
  }
  return Number.parseInt(value, 10);
};

const validateAllowance = value => {
  return !Number.isNaN(value) && value >= 0;
};

const updateEntitlementEditField = (key, value) => {
  // Validate usage fields
  if (key === "usage.allowance" || key === "usage.remainingAllowance") {
    if (!validateAllowance(value)) {
      return;
    }
  }

  // Handle nested keys with dot notation (e.g., 'usage.allowance')
  if (key.includes(".")) {
    const [parentKey, childKey] = key.split(".");
    store.dispatch("licensing/updateEntitlementEditField", {
      key: parentKey,
      value: {
        ...store.state.licensing.entitlementEdit[parentKey],
        [childKey]: value
      }
    });
  } else {
    store.dispatch("licensing/updateEntitlementEditField", { key, value });
  }
};

const validate = () => {
  emit("updateEntitlement");
  state.editOwnerModalIsOpen = false;
  state.confirmOwnerDeletion = false;
};

const confirmOwnerDeletion = () => {
  state.ownerId = null;
  state.ownerType = null;
  store.dispatch("licensing/updateEntitlementEditField", {
    key: "ownerId",
    value: null
  });
  store.dispatch("licensing/updateEntitlementEditField", {
    key: "ownerType",
    value: null
  });
  validate();
};

// Check if the current state is valid for saving
const isValidForSave = () => {
  // Validate seats
  if (!validateSeats(state.seats)) {
    return false;
  }

  // Validate allowance fields
  if (state.allowance !== undefined && !validateAllowance(state.allowance)) {
    return false;
  }

  if (
    state.remainingAllowance !== undefined &&
    !validateAllowance(state.remainingAllowance)
  ) {
    return false;
  }

  // Validate owner - block save if owner is invalid
  if (state.isOwnerInvalid) {
    return false;
  }

  return true;
};

// Expose validation to parent
defineExpose({
  isValidForSave
});
</script>
